/**
 * One-page printed leave-behind. Mirrors the estimate page hierarchy and is
 * built only from customer-safe data: no VPP program value, no rep controls,
 * no solar, no emoji. Temporary/placeholder rates appear as a footnote here
 * (they are not shown on screen to the customer).
 */
import { REGIONS, TDUS, TERM_OPTIONS } from "../../config";
import type { BidResult } from "../../engine/bid";
import type { AppState } from "../state";
import { formatAssumption, kwh, printAssumptions, provenance, rateFlags, termRangeLabel, termView, usd } from "../viewmodel";
import { breakdownHtml, heroCopy, includedHtml, valuePropsHtml } from "./bid";

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

/** Internal doc references read oddly on a customer handout. */
function customerSource(src: string): string {
  return /^(docs\/|DESIGN|ES Solar)/.test(src) ? "Octopus Energy Texas bid assumption" : src.replace(/\s*\(DESIGN[^)]*\)/g, "");
}

export function printSheetHtml(bid: BidResult, state: AppState, today: Date = new Date()): string {
  const v = termView(bid, state.term);
  const hero = heroCopy(v);
  const flags = rateFlags(bid);
  const flagged = new Set(flags.map((f) => f.key));
  const others = TERM_OPTIONS.filter((t) => t !== state.term)
    .map((t) => {
      const tv = termView(bid, t);
      return `${t} months: ${usd(tv.savingsAvg)}/mo (${usd(tv.savingsTotal)} total)`;
    })
    .join(" · ");

  const rows = printAssumptions(bid, state.term)
    .map((a) => {
      const p = provenance(a);
      const mark = flagged.has(a.key) || a.placeholder ? "*" : "";
      return `<tr><td>${esc(a.label)}${mark}${a.overridden ? " <em>(rep-adjusted)</em>" : ""}</td>
        <td class="num">${esc(formatAssumption(a))}</td><td class="date">${p.date}</td><td class="src">${esc(customerSource(p.source))}</td></tr>`;
    })
    .join("");
  const footnotes = flags.map((f) => `<p class="ps-fine">* ${esc(f.text)}.</p>`).join("");

  return `
  <header class="ps-head">
    <div class="ps-brand">octopus<b>energy</b></div>
    <div class="ps-meta">Octopus Energy Texas Savings Estimate · Prepared ${today.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
  </header>
  <section class="ps-hero">
    <p class="hero-label">${hero.label}</p>
    <p class="ps-big">${hero.amount}<small>/mo</small></p>
    <p class="hero-line">${usd(Math.abs(v.savingsTotal))} over ${v.term} months · ${termRangeLabel(state.startMonth, v.term, today)}</p>
  </section>
  <div class="compare">
    <div class="tile"><span class="tile-label">Today</span><span class="tile-val">${usd(v.todayAvg)}<small>/mo</small></span></div>
    <div class="tile"><span class="tile-label">With Octopus</span><span class="tile-val">${usd(v.octopusAvg)}<small>/mo</small></span></div>
  </div>
  ${includedHtml(state)}
  ${breakdownHtml(v)}
  <p class="ps-fine">Other plan lengths: ${others}. ${TDUS[bid.tdu].name} · ${REGIONS[bid.region].name} · ${kwh(bid.energy.homeAnnualKwh + bid.energy.evAnnualKwh)}/yr.</p>
  ${valuePropsHtml()}
  <section class="ps-assume">
    <h2 class="section-label">Assumptions and rates</h2>
    <table class="atable"><thead><tr><th>Item</th><th>Value</th><th>Effective</th><th>Source</th></tr></thead><tbody>${rows}</tbody></table>
    ${footnotes}
    <p class="ps-fine">Estimate only, based on the usage and current-plan details provided. Monthly amounts are rounded to the cent; totals are the sum of the rounded months. Utility (TDU) delivery charges are passed through at cost and may change when the PUCT approves new rates. Totals cover the plan term only.</p>
  </section>`;
}
